import React, { useState } from 'react';
import { AppBar, Box, Button, Container, Divider, Link as MuiLink, Toolbar, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants';
import Logo from '@components/Logo';
import AccountDeletionFlowModal from '@components/accountDeletion/AccountDeletionFlowModal';

const AccountDeletionInfoPage: React.FC = () => {
  const navigate = useNavigate();
  const [flowOpen, setFlowOpen] = useState(false);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Logo />
          </Box>
          <Button variant="outlined" component={RouterLink} to={ROUTES.LOGIN}>
            Entrar
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md">
        <Box sx={{ mb: 3, pt: { xs: 4, md: 6 }, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="text" onClick={() => navigate(ROUTES.HOME)}>
            Voltar para a página inicial
          </Button>
        </Box>

        <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
          Exclusão de conta
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Entenda como funciona a exclusão e como seguir com a solicitação.
        </Typography>

        <Box
          sx={{
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            p: { xs: 2.5, md: 4 },
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            Importante
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
            A exclusão é um processo <strong>irreversível</strong> e remove os dados operacionais da organização associada à conta.
            Somente o <strong>criador da conta</strong> (e-mail de contato do cadastro) pode solicitar a exclusão.
          </Typography>

          <Divider sx={{ my: 2.5 }} />

          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            Como solicitar a exclusão
          </Typography>
          <Box component="ol" sx={{ pl: 3, lineHeight: 1.9, mb: 2 }}>
            <li>Inicie a solicitação e valide sua identidade (e-mail e senha do criador da conta).</li>
            <li>Confirme o código de 6 dígitos enviado por e-mail.</li>
            <li>Confirme a palavra <strong>EXCLUIR</strong> para iniciar o processo.</li>
          </Box>

          <Button variant="contained" color="error" fullWidth onClick={() => setFlowOpen(true)} sx={{ mb: 2 }}>
            Iniciar exclusão da conta
          </Button>

          <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
            Após confirmar a palavra <strong>EXCLUIR</strong>, o acesso ao sistema é <strong>bloqueado imediatamente</strong> e você poderá acompanhar o
            <strong> progresso em tempo real</strong> até a conclusão. Na maioria dos casos, o processo conclui em poucos minutos (dependendo do volume de dados).
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            Retenção mínima
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
            Por obrigações legais, registros financeiros do faturamento podem ser retidos por até <strong>5 anos</strong>. Logs de auditoria são retidos por até{' '}
            <strong>90 dias</strong>.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            Após entrar no sistema
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
            Se você estiver com acesso normal ao sistema, também pode iniciar a exclusão em <strong>Configurações</strong> → <strong>Exclusão de conta</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Se você estiver na tela de login, use o atalho <strong>“Quero excluir minha conta”</strong>.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            Contato
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            Em caso de dúvidas, entre em contato em <MuiLink href="mailto:contato@vover.com.br">contato@vover.com.br</MuiLink>.
          </Typography>
        </Box>
      </Container>

      <AccountDeletionFlowModal open={flowOpen} onClose={() => setFlowOpen(false)} mode="public" />
    </Box>
  );
};

export default AccountDeletionInfoPage;

